import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  useBillingCodes,
  useCreateTimeEntry,
  useUpdateTimeEntry,
} from '../../hooks/queries/useTimeEntries';
import { parseDuration, type TimeEntry } from '../../services/timeTracking/timeTrackingService';
import { cn } from '../../lib/cn';
import { prefillToFormValues, type TimerPrefill } from './timeEntryPrefill';

interface TimeEntryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dossierId: string;
  /** Si fourni, on édite ; sinon on crée */
  entry?: TimeEntry | null;
  /** Si fourni (depuis le chrono), pré-remplit les champs ; durée verrouillée. */
  prefill?: TimerPrefill | null;
  /** Appelé après un enregistrement réussi, avant onOpenChange(false). */
  onSaveSuccess?: () => void;
}

interface FormValues {
  date: string;        // YYYY-MM-DD
  startTime: string;   // HH:mm
  durationText: string;
  is_billable: boolean;
  billing_code: string;
  description: string;
}

const DEFAULT_VALUES: FormValues = {
  date: format(new Date(), 'yyyy-MM-dd'),
  startTime: '09:00',
  durationText: '',
  is_billable: true,
  billing_code: '',
  description: '',
};

export function TimeEntryModal({ open, onOpenChange, dossierId, entry, prefill, onSaveSuccess }: TimeEntryModalProps) {
  const isEdit = !!entry;
  const createMut = useCreateTimeEntry();
  const updateMut = useUpdateTimeEntry();
  const isPending = createMut.isPending || updateMut.isPending;

  const { data: codes } = useBillingCodes();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: DEFAULT_VALUES });

  const [durationUnlocked, setDurationUnlocked] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDurationUnlocked(false);
    if (entry) {
      const start = new Date(entry.started_at);
      reset({
        date: format(start, 'yyyy-MM-dd'),
        startTime: format(start, 'HH:mm'),
        durationText: String(entry.duration_minutes),
        is_billable: entry.is_billable,
        billing_code: entry.billing_code ?? '',
        description: entry.description ?? '',
      });
    } else if (prefill) {
      reset(prefillToFormValues(prefill));
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [open, entry, prefill, reset]);

  const isBillable = watch('is_billable');

  async function onSubmit(values: FormValues) {
    const minutes = parseDuration(values.durationText);
    if (minutes === null || minutes <= 0) {
      toast.error('Durée invalide. Formats acceptés : 1h30, 1:30, 90, 1.5h');
      return;
    }
    const startedAt = new Date(`${values.date}T${values.startTime}:00`);
    if (Number.isNaN(startedAt.getTime())) {
      toast.error('Date ou heure invalide');
      return;
    }

    try {
      if (isEdit && entry) {
        await updateMut.mutateAsync({
          id: entry.id,
          input: {
            started_at: startedAt.toISOString(),
            duration_minutes: minutes,
            is_billable: values.is_billable,
            billing_code: values.billing_code || null,
            description: values.description || null,
          },
        });
        toast.success('Prestation mise à jour');
      } else {
        await createMut.mutateAsync({
          dossier_id: dossierId,
          started_at: startedAt.toISOString(),
          duration_minutes: minutes,
          is_billable: values.is_billable,
          billing_code: values.billing_code || null,
          description: values.description || null,
        });
        toast.success('Prestation enregistrée');
      }
      onSaveSuccess?.();
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error(`Erreur : ${message}`);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
            'w-full max-w-md max-h-[90vh] overflow-y-auto',
            'bg-cime-surface rounded-cime border border-neutral-200 shadow-xl',
          )}
        >
          <div className="flex items-center justify-between p-6 border-b border-neutral-200">
            <Dialog.Title className="font-heading text-lg font-semibold text-neutral-900">
              {isEdit ? 'Modifier la prestation' : prefill ? 'Enregistrer le temps chronométré' : 'Nouvelle prestation'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="p-6 space-y-4">
            {/* Date + heure */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="te-date" className="text-sm font-medium text-neutral-700">
                  Date <span className="text-red-500">*</span>
                </label>
                <Input id="te-date" type="date" {...register('date', { required: true })} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="te-time" className="text-sm font-medium text-neutral-700">
                  Heure de début
                </label>
                <Input id="te-time" type="time" {...register('startTime')} />
              </div>
            </div>

            {/* Durée */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="te-duration" className="text-sm font-medium text-neutral-700">
                Durée <span className="text-red-500">*</span>
              </label>
              <Input
                id="te-duration"
                placeholder="1h30, 1:30, 90, 1.5h"
                aria-invalid={!!errors.durationText}
                readOnly={!!prefill && !durationUnlocked}
                className={cn(!!prefill && !durationUnlocked && 'bg-neutral-100 text-neutral-500')}
                {...register('durationText', { required: 'Durée requise' })}
              />
              {!!prefill && !durationUnlocked && (
                <button
                  type="button"
                  onClick={() => setDurationUnlocked(true)}
                  className="mt-1 text-xs text-cime-accent underline"
                >
                  Ajuster la durée
                </button>
              )}
              {errors.durationText && (
                <p className="text-xs text-red-600">{errors.durationText.message}</p>
              )}
            </div>

            {/* Billable toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-neutral-700">Facturable</span>
              <button
                type="button"
                role="switch"
                aria-checked={isBillable}
                onClick={() => setValue('is_billable', !isBillable, { shouldDirty: true })}
                className={cn(
                  'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  isBillable ? 'bg-cime-primary' : 'bg-neutral-300',
                )}
              >
                <span
                  className={cn(
                    'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
                    isBillable ? 'translate-x-6' : 'translate-x-1',
                  )}
                />
              </button>
            </div>

            {/* Code facturation */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="te-code" className="text-sm font-medium text-neutral-700">
                Code <span className="text-neutral-400 text-xs font-normal">(optionnel)</span>
              </label>
              <Input
                id="te-code"
                list="te-codes-list"
                placeholder="Ex: REDACTION, AUDIENCE, RDV…"
                {...register('billing_code')}
              />
              <datalist id="te-codes-list">
                {codes?.map((c) => (
                  <option key={c.code} value={c.code} />
                ))}
              </datalist>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="te-desc" className="text-sm font-medium text-neutral-700">
                Description <span className="text-neutral-400 text-xs font-normal">(optionnel)</span>
              </label>
              <textarea
                id="te-desc"
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-cime border border-neutral-300 bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent"
                placeholder="Détail de la prestation effectuée"
                {...register('description')}
              />
            </div>

            {/* Footer */}
            <div className="border-t border-neutral-200 pt-4 flex gap-3 justify-end">
              <Dialog.Close asChild>
                <Button type="button" variant="ghost" disabled={isPending}>
                  Annuler
                </Button>
              </Dialog.Close>
              <Button type="submit" variant="primary" isLoading={isPending}>
                {isEdit ? 'Enregistrer' : 'Ajouter'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
